import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface BankAccount {
  id: string;
  bank_name: string;
  account_number: string;
  account_holder: string;
  branch: string | null;
  is_default: boolean;
  created_at: string;
}

interface KycData {
  status: string;
  id_type: string | null;
  id_number: string | null;
  full_name_on_id: string | null;
  date_of_birth: string | null;
  id_front_image: string | null;
  id_back_image: string | null;
  selfie_image: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
}

interface UserProfile {
  id: string;
  username: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  trading_code: string;
  two_factor_enabled: boolean;
  created_at: string;
}

interface WalletData {
  balance: number;
  balance_locked: number;
  total_profit_loss: number;
}

interface OrderData {
  type: string;
  side: string;
  volume: number;
  entry_price: number;
  take_profit: number | null;
  stop_loss: number | null;
  status: string;
  profit_loss: number | null;
  fee: number | null;
  created_at: string;
  closed_at: string | null;
}

interface LoginEntry {
  ip_address: string;
  user_agent: string;
  status: string;
  created_at: string;
}

interface DepositReq {
  amount: number;
  status: string;
  transaction_code: string | null;
  created_at: string;
  approved_at: string | null;
}

interface WithdrawReq {
  amount: number;
  status: string;
  fee: number | null;
  created_at: string;
  approved_at: string | null;
}

export interface UserDetail {
  profile: UserProfile;
  wallet: WalletData;
  kyc: KycData;
  bank_accounts: BankAccount[];
  orders: OrderData[];
  login_history: LoginEntry[];
  deposit_requests: DepositReq[];
  withdraw_requests: WithdrawReq[];
}

interface Props {
  user: {
    id: string;
    full_name: string | null;
    username: string;
    trading_code: string;
    is_active: boolean;
  };
  detail: UserDetail | null;
  detailLoading: boolean;
  onClose: () => void;
  onToggleActive: () => void;
  toggleLoading: boolean;
  onRefresh: () => Promise<void>;
}

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatShortDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatMoney = (n: number) => n.toLocaleString('vi-VN') + 'đ';

export default function UserDetailModal({ user, detail, detailLoading, onClose, onToggleActive, toggleLoading, onRefresh }: Props) {
  const [detailTab, setDetailTab] = useState<'info' | 'orders' | 'kyc' | 'login'>('info');
  const [editingKyc, setEditingKyc] = useState(false);
  const [kycForm, setKycForm] = useState({
    id_type: '', id_number: '', full_name_on_id: '', date_of_birth: '', status: '', rejection_reason: '',
  });
  const [savingKyc, setSavingKyc] = useState(false);
  const [kycError, setKycError] = useState('');
  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const [bankForm, setBankForm] = useState({ bank_name: '', account_number: '', account_holder: '', branch: '', is_default: false });
  const [savingBank, setSavingBank] = useState(false);
  const [bankError, setBankError] = useState('');

  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  const kycMeta: Record<string, { label: string; cls: string }> = {
    verified: { label: 'Đã xác minh', cls: 'bg-emerald-500/15 text-emerald-400' },
    pending: { label: 'Chờ duyệt', cls: 'bg-accent-500/15 text-accent-400' },
    rejected: { label: 'Từ chối', cls: 'bg-rose-500/15 text-rose-400' },
    none: { label: 'Chưa gửi', cls: 'bg-secondary-500/15 text-secondary-400' },
  };

  const orderStatusMeta: Record<string, string> = {
    open: 'Đang mở',
    closed: 'Đã đóng',
    cancelled: 'Đã hủy',
    settled: 'Đã thanh toán',
  };

  const startEditKyc = () => {
    if (!detail) return;
    setKycForm({
      id_type: detail.kyc.id_type || '',
      id_number: detail.kyc.id_number || '',
      full_name_on_id: detail.kyc.full_name_on_id || '',
      date_of_birth: detail.kyc.date_of_birth || '',
      status: detail.kyc.status === 'none' ? '' : detail.kyc.status,
      rejection_reason: detail.kyc.rejection_reason || '',
    });
    setEditingKyc(true);
    setKycError('');
  };

  const saveKyc = async () => {
    if (!detail) return;
    setSavingKyc(true);
    setKycError('');
    try {
      const { error: rpcError } = await supabase.rpc('update_kyc_info', {
        p_user_id: user.id,
        p_id_type: kycForm.id_type || null,
        p_id_number: kycForm.id_number || null,
        p_full_name_on_id: kycForm.full_name_on_id || null,
        p_date_of_birth: kycForm.date_of_birth || null,
        p_status: kycForm.status || null,
        p_rejection_reason: kycForm.rejection_reason || null,
      });
      if (rpcError) throw rpcError;
      setEditingKyc(false);
      await onRefresh();
    } catch (err) {
      setKycError(err instanceof Error ? err.message : 'Lưu thất bại.');
    } finally {
      setSavingKyc(false);
    }
  };

  const startEditBank = (bank: BankAccount) => {
    setBankForm({
      bank_name: bank.bank_name,
      account_number: bank.account_number,
      account_holder: bank.account_holder,
      branch: bank.branch || '',
      is_default: bank.is_default,
    });
    setEditingBankId(bank.id);
    setBankError('');
  };

  const saveBank = async () => {
    if (!editingBankId) return;
    setSavingBank(true);
    setBankError('');
    try {
      const { error: rpcError } = await supabase.rpc('update_bank_account', {
        p_bank_account_id: editingBankId,
        p_bank_name: bankForm.bank_name || null,
        p_account_number: bankForm.account_number || null,
        p_account_holder: bankForm.account_holder || null,
        p_branch: bankForm.branch || null,
        p_is_default: bankForm.is_default,
      });
      if (rpcError) throw rpcError;
      setEditingBankId(null);
      await onRefresh();
    } catch (err) {
      setBankError(err instanceof Error ? err.message : 'Lưu thất bại.');
    } finally {
      setSavingBank(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/50" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-background-200/60 bg-background-100 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0">
              <span className="text-base font-bold text-primary-400">{initials(user.full_name || user.username)}</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground-950">{user.full_name || user.username}</h2>
              <p className="text-xs text-foreground-600">Mã giao dịch: <span className="font-mono text-foreground-800">{user.trading_code}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-800 hover:bg-background-200/40 cursor-pointer transition-colors">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-5 py-3 border-b border-background-200/60">
          <div className="flex rounded-full bg-background-50 border border-background-300/60 p-1">
            {(['info', 'orders', 'kyc', 'login'] as const).map((t) => (
              <button key={t} onClick={() => setDetailTab(t)} className={`flex-1 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${detailTab === t ? 'bg-primary-500 text-background-950' : 'text-foreground-600'}`}>
                {t === 'info' ? 'Thông tin & Giao dịch' : t === 'orders' ? 'Lệnh giao dịch' : t === 'kyc' ? 'Xác minh KYC' : 'Nhật ký đăng nhập'}
              </button>
            ))}
          </div>
        </div>

        {detailLoading && <div className="px-5 py-10 text-center text-sm text-foreground-600">Đang tải chi tiết...</div>}

        {!detailLoading && detail && (
          <div className="px-5 py-4 space-y-4">
            {detailTab === 'info' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Thông tin cá nhân</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-foreground-600">Tên đăng nhập</span><span className="font-semibold text-foreground-950">{detail.profile.username}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Họ tên</span><span className="font-semibold text-foreground-950">{detail.profile.full_name || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Email</span><span className="font-semibold text-foreground-950">{detail.profile.email || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">SĐT</span><span className="font-semibold text-foreground-950">{detail.profile.phone || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Ngày đăng ký</span><span className="font-semibold text-foreground-950">{formatDate(detail.profile.created_at)}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Xác thực 2 bước</span><span className={`font-semibold ${detail.profile.two_factor_enabled ? 'text-emerald-400' : 'text-foreground-500'}`}>{detail.profile.two_factor_enabled ? 'Bật' : 'Tắt'}</span></div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Tài khoản ví</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-foreground-600">Số dư khả dụng</span><span className="font-semibold text-emerald-400">{formatMoney(Number(detail.wallet.balance))}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Số dư khóa</span><span className="font-semibold text-foreground-950">{formatMoney(Number(detail.wallet.balance_locked))}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Tổng P/L</span><span className={`font-semibold ${Number(detail.wallet.total_profit_loss) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatMoney(Number(detail.wallet.total_profit_loss))}</span></div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500">Tài khoản ngân hàng ({detail.bank_accounts.length})</h3>
                    <button onClick={onToggleActive} disabled={toggleLoading} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${user.is_active ? 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25' : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'}`}>
                      {user.is_active ? 'Khóa tài khoản' : 'Mở khóa'}
                    </button>
                  </div>
                  {detail.bank_accounts.length === 0 ? (
                    <p className="text-sm text-foreground-500">Chưa có tài khoản ngân hàng.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.bank_accounts.map((b) => (
                        <div key={b.id}>
                          {editingBankId === b.id ? (
                            <div className="rounded-md bg-background-100 border border-primary-500/20 p-3 space-y-2">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[10px] font-semibold text-foreground-600 uppercase">Ngân hàng</label>
                                  <input value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-semibold text-foreground-600 uppercase">Số tài khoản</label>
                                  <input value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-semibold text-foreground-600 uppercase">Chủ tài khoản</label>
                                  <input value={bankForm.account_holder} onChange={(e) => setBankForm({ ...bankForm, account_holder: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-semibold text-foreground-600 uppercase">Chi nhánh</label>
                                  <input value={bankForm.branch} onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                                </div>
                              </div>
                              <label className="flex items-center gap-2 text-sm text-foreground-700">
                                <input type="checkbox" checked={bankForm.is_default} onChange={(e) => setBankForm({ ...bankForm, is_default: e.target.checked })} className="accent-primary-500" />
                                Đặt làm mặc định
                              </label>
                              {bankError && <p className="text-xs text-rose-400">{bankError}</p>}
                              <div className="flex gap-2">
                                <button onClick={saveBank} disabled={savingBank} className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap">{savingBank ? 'Đang lưu...' : 'Lưu'}</button>
                                <button onClick={() => setEditingBankId(null)} className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-xs font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Hủy</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between rounded-md bg-background-100 border border-background-200/40 px-3 py-2">
                              <div>
                                <p className="text-sm font-semibold text-foreground-950">{b.bank_name}</p>
                                <p className="text-xs text-foreground-600">{b.account_holder} • {b.account_number}</p>
                                {b.branch && <p className="text-[10px] text-foreground-500">{b.branch}</p>}
                              </div>
                              <div className="flex items-center gap-2">
                                {b.is_default && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-500/15 text-primary-400">Mặc định</span>}
                                <button onClick={() => startEditBank(b)} className="px-2 py-1 rounded-md border border-background-300/60 text-[10px] font-semibold text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer whitespace-nowrap transition-colors">
                                  <i className="ri-pencil-line mr-1"></i>Sửa
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Yêu cầu nạp tiền ({detail.deposit_requests.length})</h3>
                    {detail.deposit_requests.length === 0 ? <p className="text-sm text-foreground-500">Chưa có.</p> : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {detail.deposit_requests.map((d, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <div><span className="font-semibold text-foreground-950">{formatMoney(Number(d.amount))}</span><span className="text-xs text-foreground-500 ml-2">{formatShortDate(d.created_at)}</span></div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${d.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : d.status === 'rejected' ? 'bg-rose-500/15 text-rose-400' : 'bg-accent-500/15 text-accent-400'}`}>{d.status === 'approved' ? 'Đã duyệt' : d.status === 'rejected' ? 'Từ chối' : 'Chờ duyệt'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Yêu cầu rút tiền ({detail.withdraw_requests.length})</h3>
                    {detail.withdraw_requests.length === 0 ? <p className="text-sm text-foreground-500">Chưa có.</p> : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {detail.withdraw_requests.map((w, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <div><span className="font-semibold text-foreground-950">{formatMoney(Number(w.amount))}</span>{w.fee ? <span className="text-xs text-foreground-500 ml-2">Phí: {formatMoney(Number(w.fee))}</span> : null}</div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${w.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : w.status === 'rejected' ? 'bg-rose-500/15 text-rose-400' : 'bg-accent-500/15 text-accent-400'}`}>{w.status === 'approved' ? 'Đã duyệt' : w.status === 'rejected' ? 'Từ chối' : 'Chờ duyệt'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {detailTab === 'orders' && (
              <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Lệnh giao dịch ({detail.orders.length})</h3>
                {detail.orders.length === 0 ? <p className="text-sm text-foreground-500">Chưa có lệnh nào.</p> : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {detail.orders.map((o, i) => (
                      <div key={i} className="rounded-md bg-background-100 border border-background-200/40 px-3 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-foreground-950">{o.side === 'buy' ? 'Mua' : 'Bán'} {o.type === 'limit' ? 'Limit' : 'Market'} {formatMoney(Number(o.volume))}</span>
                          <span className="text-[10px] text-foreground-500">{formatDate(o.created_at)}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-foreground-600">
                          <span>Giá vào: <b className="text-foreground-950">{Number(o.entry_price).toLocaleString('vi-VN')}đ</b></span>
                          {o.take_profit && <span>TP: <b className="text-emerald-400">{Number(o.take_profit).toLocaleString('vi-VN')}đ</b></span>}
                          {o.stop_loss && <span>SL: <b className="text-rose-400">{Number(o.stop_loss).toLocaleString('vi-VN')}đ</b></span>}
                          {o.fee !== null && <span>Phí: {formatMoney(Number(o.fee))}</span>}
                          {o.profit_loss !== null && <span className={Number(o.profit_loss) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>P/L: {formatMoney(Number(o.profit_loss))}</span>}
                        </div>
                        <div className="mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${o.status === 'closed' || o.status === 'settled' ? 'bg-emerald-500/15 text-emerald-400' : o.status === 'cancelled' ? 'bg-rose-500/15 text-rose-400' : 'bg-accent-500/15 text-accent-400'}`}>{orderStatusMeta[o.status] || o.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {detailTab === 'kyc' && (
              <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500">Xác minh danh tính (KYC)</h3>
                  {!editingKyc && detail.kyc.status !== 'none' && (
                    <button onClick={startEditKyc} className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-pencil-line mr-1"></i>Sửa KYC
                    </button>
                  )}
                </div>
                {detail.kyc.status === 'none' ? (
                  <p className="text-sm text-foreground-500">Người dùng chưa gửi yêu cầu xác minh KYC.</p>
                ) : editingKyc ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-semibold text-foreground-600 uppercase">Loại giấy tờ</label>
                        <select value={kycForm.id_type} onChange={(e) => setKycForm({ ...kycForm, id_type: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500">
                          <option value="">Chọn...</option>
                          <option value="CCCD">CCCD</option>
                          <option value="CMND">CMND</option>
                          <option value="Hộ chiếu">Hộ chiếu</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-foreground-600 uppercase">Trạng thái</label>
                        <select value={kycForm.status} onChange={(e) => setKycForm({ ...kycForm, status: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500">
                          <option value="pending">Chờ duyệt</option>
                          <option value="verified">Đã xác minh</option>
                          <option value="rejected">Từ chối</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-foreground-600 uppercase">Số giấy tờ</label>
                        <input value={kycForm.id_number} onChange={(e) => setKycForm({ ...kycForm, id_number: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-foreground-600 uppercase">Ngày sinh</label>
                        <input type="date" value={kycForm.date_of_birth} onChange={(e) => setKycForm({ ...kycForm, date_of_birth: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-[10px] font-semibold text-foreground-600 uppercase">Họ tên trên giấy tờ</label>
                        <input value={kycForm.full_name_on_id} onChange={(e) => setKycForm({ ...kycForm, full_name_on_id: e.target.value })} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500" />
                      </div>
                      {kycForm.status === 'rejected' && (
                        <div className="sm:col-span-2">
                          <label className="text-[10px] font-semibold text-foreground-600 uppercase">Lý do từ chối</label>
                          <textarea value={kycForm.rejection_reason} onChange={(e) => setKycForm({ ...kycForm, rejection_reason: e.target.value })} rows={2} className="w-full px-2 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 resize-none" />
                        </div>
                      )}
                    </div>
                    {kycError && <p className="text-xs text-rose-400">{kycError}</p>}
                    <div className="flex gap-2">
                      <button onClick={saveKyc} disabled={savingKyc} className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap">{savingKyc ? 'Đang lưu...' : 'Lưu'}</button>
                      <button onClick={() => setEditingKyc(false)} className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-xs font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Hủy</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${kycMeta[detail.kyc.status]?.cls || 'bg-secondary-500/15 text-secondary-400'}`}>{kycMeta[detail.kyc.status]?.label || detail.kyc.status}</span>
                      {detail.kyc.submitted_at && <span className="text-xs text-foreground-500">Gửi: {formatDate(detail.kyc.submitted_at)}</span>}
                      {detail.kyc.reviewed_at && <span className="text-xs text-foreground-500">Duyệt: {formatDate(detail.kyc.reviewed_at)}</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between"><span className="text-foreground-600">Loại giấy tờ</span><span className="font-semibold text-foreground-950">{detail.kyc.id_type || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Số giấy tờ</span><span className="font-semibold text-foreground-950">{detail.kyc.id_number || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Họ tên trên giấy tờ</span><span className="font-semibold text-foreground-950">{detail.kyc.full_name_on_id || '—'}</span></div>
                      <div className="flex justify-between"><span className="text-foreground-600">Ngày sinh</span><span className="font-semibold text-foreground-950">{detail.kyc.date_of_birth || '—'}</span></div>
                    </div>
                    {detail.kyc.rejection_reason && (
                      <div className="rounded-md bg-rose-500/10 border border-rose-500/20 p-3 text-sm text-rose-400"><span className="font-semibold">Lý do từ chối:</span> {detail.kyc.rejection_reason}</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      {detail.kyc.id_front_image && (
                        <a href={detail.kyc.id_front_image} target="_blank" rel="noreferrer noopener" className="block rounded-md overflow-hidden border border-background-200/60 bg-background-100">
                          <img src={detail.kyc.id_front_image} alt="Mặt trước" className="w-full h-32 object-cover" />
                          <p className="text-center text-[10px] text-foreground-600 py-1.5">Mặt trước</p>
                        </a>
                      )}
                      {detail.kyc.id_back_image && (
                        <a href={detail.kyc.id_back_image} target="_blank" rel="noreferrer noopener" className="block rounded-md overflow-hidden border border-background-200/60 bg-background-100">
                          <img src={detail.kyc.id_back_image} alt="Mặt sau" className="w-full h-32 object-cover" />
                          <p className="text-center text-[10px] text-foreground-600 py-1.5">Mặt sau</p>
                        </a>
                      )}
                      {detail.kyc.selfie_image && (
                        <a href={detail.kyc.selfie_image} target="_blank" rel="noreferrer noopener" className="block rounded-md overflow-hidden border border-background-200/60 bg-background-100">
                          <img src={detail.kyc.selfie_image} alt="Chân dung" className="w-full h-32 object-cover" />
                          <p className="text-center text-[10px] text-foreground-600 py-1.5">Chân dung</p>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailTab === 'login' && (
              <div className="rounded-lg border border-background-200/60 bg-background-50 p-4">
                <h3 className="text-xs font-bold uppercase tracking-wide text-foreground-500 mb-3">Nhật ký đăng nhập</h3>
                {detail.login_history.length === 0 ? <p className="text-sm text-foreground-500">Chưa có bản ghi đăng nhập.</p> : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {detail.login_history.map((l, i) => (
                      <div key={i} className="flex items-center justify-between rounded-md bg-background-100 border border-background-200/40 px-3 py-2">
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{l.ip_address}</p>
                          <p className="text-xs text-foreground-500 truncate max-w-xs">{l.user_agent}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-foreground-500">{formatDate(l.created_at)}</span>
                          <p className={`text-xs font-semibold ${l.status === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>{l.status === 'success' ? 'Thành công' : 'Thất bại'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}